import { useState } from "react";
import { OperationRegistry } from "./OperationRegistry";
import { WizardOperation } from "./types";

export type UseWizardOperationControllerProps<
  TOperation extends WizardOperation,
> = {
  executeOperation: (operation: TOperation) => Promise<unknown>;
  onComplete: () => Promise<unknown>;
  onError: (error: unknown) => void;
};

export const useWizardOperationController = <
  TOperation extends WizardOperation,
>({
  executeOperation,
  onComplete,
  onError,
}: UseWizardOperationControllerProps<TOperation>) => {
  // we keep the operations in a stable, imperative registry
  // so that the individual steps can register/unregister/clear operations
  // without causing unnecessary re-renders
  const [operations] = useState(() => new OperationRegistry<TOperation>());

  const completeWizardWithOperations = async () => {
    try {
      await Promise.all(
        operations
          .getOperations()
          .map((operation) => executeOperation(operation))
      );
      operations.clear();
      await onComplete();
    } catch (error) {
      onError(error);
    }
  };

  return { operations, completeWizardWithOperations };
};
